// import { colors } from '../foundations';
import { css } from '@emotion/react';
import { ComponentType } from 'react';

export enum CardOrientation {
  Horizontal = 'row',
  Vertical = 'column'
}

export enum CardBarColor {
  Blue = '#0065FF',
  Orange = '#DE350B'
}
export enum CardAspectRatio {
  OneToOne = '1 / 1',
  TwoToOne = '2 / 1',
  SixteenByNine = '16 / 9',
  ThreeToTwo = '3 / 2'
}

export interface CardProps {
  cardText: string;
  cardHeader: string;
  borderHeader?: boolean;
  eyebrowText?: string;
  footerHeader?: string;
  footerText?: string;
  imageLink?: string;
  altText?: string;
  backgroundDark?: boolean;
  CallToAction?: ComponentType;
  Icon?: ComponentType;
  orientation?: CardOrientation;
  aspectRatio?: CardAspectRatio;
  hasBarTop?: boolean;
  hasBarBottom?: boolean;
  barColor?: CardBarColor;
  className?: string;
}

const orientationStyles = (orientation: CardOrientation) => css`
  display: flex;
  flex-direction: ${orientation};
  height: 100%;
`;
const textStyles = (bold: boolean) => css`
  font-weight: ${bold ? 700 : 400};
  font-size: 1rem;
  line-height: 1.5rem;
  margin: 8px auto;
`;

const containerStyles = (backgroundColor: string) => css`
  background: ${backgroundColor};
  border-radius: 2px;
  box-shadow: 0px 4px 6px 4px #42526e29;
  color: ${backgroundColor !== '#FFFFFF' ? '#F4F5F7' : '#253858'};
  display: flex;
  flex-direction: column;
`;

const imageStyles = (ratio: CardAspectRatio) => css`
  aspect-ratio: ${ratio};
`;

const barStyles = (color: CardBarColor, isTop: boolean) => css`
  background-color: ${color};
  margin: 0;
  padding: 0;
  width: 100%;
  height: ${color === CardBarColor.Blue ? 10 : 8}px;
  ${isTop
    ? 'border-top-right-radius: 4px; border-top-left-radius: 4px;'
    : 'border-bottom-right-radius: 4px; border-bottom-left-radius: 4px;'}
`;

const borderHeaderStyles = css`
  background: #f4f5f7;
  color: #253858;
  font-size: 1.25rem;
  line-height: 1.5rem;
  margin: 0;
  padding-block: 14px;
  padding-left: 16px;
`;

const eyebrowStyles = css`
  color: #5c5e60;
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 1px;
  line-height: 1.25rem;
  margin: 0;
`;

export const Card = (props: CardProps) => {
  const {
    backgroundDark,
    cardText,
    cardHeader,
    eyebrowText,
    footerHeader,
    footerText,
    imageLink,
    altText,
    CallToAction,
    Icon,
    className
  } = props;
  let {
    borderHeader,
    aspectRatio,
    orientation,
    hasBarTop,
    hasBarBottom,
    barColor
  } = props;
  //replace with colors['lofi'].background['items-light'] when typed properly
  const backgroundColor = backgroundDark ? '#253858' : '#FFFFFF';
  aspectRatio =
    aspectRatio === undefined ? CardAspectRatio.OneToOne : aspectRatio;
  orientation =
    orientation === undefined ? CardOrientation.Vertical : orientation;
  hasBarTop = hasBarTop || false;
  hasBarBottom = hasBarBottom || false;
  borderHeader = hasBarBottom || false;

  if (barColor && !(hasBarTop || hasBarBottom)) {
    throw new Error('Cannot specify bar color without a bar allowed.');
  }

  barColor = barColor === undefined ? CardBarColor.Blue : barColor;
  if (
    !(backgroundColor && aspectRatio && orientation && cardText && cardHeader)
  ) {
    throw new Error(
      'Please verify all props passed in are non-Null and not undefined.'
    );
  }

  if (imageLink && !altText) {
    throw new Error(
      'If using an image, please provide an alt text for accessibility purposes!'
    );
  }

  return (
    <div className={className} css={containerStyles(backgroundColor)}>
      {hasBarTop && <div css={barStyles(barColor, true)} />}
      {borderHeader && <h4 css={borderHeaderStyles}>{cardHeader}</h4>}
      <div css={orientationStyles(orientation)}>
        {imageLink && (
          <img
            css={imageStyles(aspectRatio)}
            src={imageLink}
            alt={altText}
          ></img>
        )}
        <div
          css={css`
            padding: 24px 16px 24px 16px;
          `}
        >
          {!Icon && eyebrowText && <h6 css={eyebrowStyles}>{eyebrowText}</h6>}
          {Icon && <Icon />}
          {!borderHeader && (
            <h3
              css={css`
                font-size: 1.5rem;
                line-height: 2rem;
                margin: 8px auto;
              `}
            >
              {cardHeader}
            </h3>
          )}
          <p css={textStyles(false)}>{cardText}</p>
          {footerHeader && <h4 css={textStyles(true)}>{footerHeader}</h4>}
          {footerText && <p css={textStyles(false)}>{footerText}</p>}
          {CallToAction && <CallToAction />}
        </div>
      </div>
      {hasBarBottom && <div css={barStyles(barColor, false)} />}
    </div>
  );
};
